import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import type { ESBBaseError } from '@appTypes/esb/errors';
import { councilUnitsApi } from '@modules/councilUnits/api';
import { rosterApi } from '@modules/roster/api';
import { youthProfileApi } from '@modules/youthProfile/api';
import { intl } from '@shared/localization/translate';
import toastService from '@toasts/service';

const WHITELISTED_ENDPOINTS = [
  youthProfileApi.endpoints.getParentGuardianInvitation,
  youthProfileApi.endpoints.getMembershipRegistrations,
  youthProfileApi.endpoints.getRelationships,
  rosterApi.endpoints.getUnitRosterAdults,
  rosterApi.endpoints.getUnitRosterYouths,
  rosterApi.endpoints.getUnitRosterParents,
  // The import modal shows its own unauthorized notice
  councilUnitsApi.endpoints.getImportFileStatus,
];

/**
 * Show toasts messages when receiving specific ESB errors
 */
export const rtkCustomToasts: Middleware =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_api: MiddlewareAPI) => next => action => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
    if (!isRejectedWithValue(action)) return next(action);

    const payload = action.payload as FetchBaseQueryError;
    const esbErrorCode = (payload.data as ESBBaseError)?.errorCode;
    const statusCode = action.payload.status;

    const skip = WHITELISTED_ENDPOINTS.some(endpoint =>
      endpoint.matchRejected(action),
    );

    // SBL-5720: 403 errors
    if (
      !skip &&
      statusCode === 403 &&
      // error could be C406, M406, etc
      esbErrorCode?.match(/^.403/)
    ) {
      toastService.error(intl.formatMessage({ id: 'shared.apiUnauthorized' }));
    }

    return next(action);
  };
